import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, KeyRound, User, Mail, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { AccountsState } from '../types';
import { loginWithEmail as legacyLoginWithEmail, loginWithUsername as legacyLoginWithUsername } from '../logic/authService';
import { loginWithEmail as supabaseLoginWithEmail, loginWithUsername as supabaseLoginWithUsername } from '../../data/supabase/authService';
import { SUPABASE_AUTH_ENABLED, refreshProfile, getActiveProfile } from '../../data/supabase/session';
import { getRoster, rememberProfile, forgetProfile, type RosterProfile } from '../../data/supabase/deviceRoster';
import { ProfilePicker } from './ProfilePicker';
import { useAppStore } from '../../app/hooks/useAppStore';
import { DjayNarrator, DjayBubble, BrandLockup } from '../../mascot';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
/**
 * LoginScreen — Warm, mascot-guided login experience.
 *
 * Two modes: Learner (username + password) and Adult (email + password).
 * Djay guides the user with contextual messages.
 * Smooth tab transitions with animated indicator.
 *
 * 14 juli 2026: de koppelcode-inlogmethode is VERWIJDERD. Een 6-tekens code
 * (~30 bits entropie) is te zwak om toegang te geven tot de gegevens van een
 * kind. Koppelcodes bewijzen nu alleen nog een relatie (ouder ↔ kind), geen
 * identiteit. Zie `docs/project/SUPABASE_AUTH_MIGRATION.md` §3.
 *
 * R-AUTH-1 (mei 2026): Now routes ALL login attempts through `authService`
 * (`loginWithEmail` / `loginWithUsername`).
 *
 * This activates in the actual UI:
 *  - Persistent rate-limiting (reload-survival, lockout)
 *  - Missing-passwordHash guard for adult roles
 *  - Lazy re-hash of legacy un-prefixed password hashes
 *  - Generic error messaging to prevent account enumeration
 */
interface LoginScreenProps {
  onLoginSuccess: (accountId: string, role?: 'learner' | 'parent' | 'teacher' | 'admin') => void;
  onBack: () => void;
  onNavigateToRegister?: () => void;
  accounts: AccountsState;
}
type LoginTab = 'learner' | 'adult';
const TAB_MESSAGES: Record<LoginTab, {
  text: string;
  emotion: 'happy' | 'encouraging';
}> = {
  learner: {
    text: 'Welkom terug! Vul je gegevens in 😊',
    emotion: 'happy'
  },
  adult: {
    text: 'Hallo! Log in met je e-mailadres.',
    emotion: 'encouraging'
  }
};
// Generic message used for both ACCOUNT_NOT_FOUND and INVALID_CREDENTIALS
// — never tell an attacker which of the two it was (account enumeration).
const GENERIC_LOGIN_ERROR = 'Gegevens kloppen niet. Controleer en probeer opnieuw.';
export function LoginScreen({
  onLoginSuccess,
  onBack,
  onNavigateToRegister,
  accounts
}: LoginScreenProps) {
  const updatePassword = useAppStore(s => s.updatePassword);
  const {
    shouldAnimate
  } = useMotionBudget();
  const [activeTab, setActiveTab] = useState<LoginTab>('learner');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);

  // ── Profielkiezer ──────────────────────────────────────────────────
  // Alleen zichtbaar als iemand het rooster op dit apparaat heeft aangezet.
  // Een tegel logt niet in: hij vult het inlogveld voor. Het wachtwoord blijft
  // altijd vereist — er staan geen sessies of sleutels op dit apparaat.
  const [roster, setRoster] = useState<RosterProfile[]>(() => SUPABASE_AUTH_ENABLED ? getRoster() : []);
  const [showPicker, setShowPicker] = useState(roster.length > 0);
  const handlePickProfile = (profile: RosterProfile) => {
    setActiveTab(profile.role === 'learner' ? 'learner' : 'adult');
    setUsername(profile.username ?? '');
    setEmail(profile.email ?? '');
    setPassword('');
    setError('');
    setShowPicker(false);
  };
  const handleForgetProfile = (id: string) => {
    forgetProfile(id);
    const next = getRoster();
    setRoster(next);
    if (next.length === 0) setShowPicker(false);
  };
  const djayMsg = error ? {
    text: 'Hmm, dat klopt niet helemaal. Probeer het nog eens!',
    emotion: 'thinking' as const
  } : TAB_MESSAGES[activeTab];
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (isLoading) return;
    setIsLoading(true);
    try {
      // ── Supabase-route ───────────────────────────────────────
      // De rol komt hier uit `public.profiles`, niet uit localStorage. Dat is
      // wat een admin daadwerkelijk in het adminportaal laat landen.
      if (SUPABASE_AUTH_ENABLED) {
        const supaResult = activeTab === 'learner' ? await supabaseLoginWithUsername(username, password) : await supabaseLoginWithEmail(email, password);
        if (supaResult.ok && supaResult.session) {
          // Wacht tot de sessie het profiel kent vóór navigeren, anders draait
          // de routing te vroeg en belandt de gebruiker terug op het
          // welkomstscherm omdat er nog geen actief account is.
          await refreshProfile();
          // Pas NA een geslaagde inlog vastleggen. Zou een mislukte poging al
          // een tegel achterlaten, dan wordt het rooster een manier om te
          // achterhalen welke accounts bestaan.
          const profile = getActiveProfile();
          if (profile) {
            rememberProfile({
              id: profile.id,
              displayName: profile.displayName ?? 'Onbekend',
              role: profile.role,
              username: profile.role === 'learner' ? profile.username : null,
              usernameNamespace: null,
              email: profile.role === 'learner' ? null : profile.email,
              avatar: null
            });
          }
          soundManager.playLevelUp();
          hapticManager.hapticSuccess();
          onLoginSuccess(supaResult.session.accountId, supaResult.session.role);
          return;
        }

        // De Supabase-service normaliseert zijn meldingen al: onbekende
        // gebruiker en fout wachtwoord geven exact dezelfde tekst, zodat je
        // niet kunt aftasten welke accounts bestaan.
        setError(supaResult.message ?? 'Gegevens kloppen niet. Probeer het opnieuw.');
        soundManager.playError();
        hapticManager.hapticError();
        setShakeKey(k => k + 1);
        return;
      }

      // ── Legacy localStorage-route (vangnet) ──────────────────
      const result = activeTab === 'learner' ? await legacyLoginWithUsername(username, password, accounts) : await legacyLoginWithEmail(email, password, accounts);
      if (result.success && result.session) {
        // Persist any lazy re-hash so future logins use the prefixed format.
        if (result.session.upgradedHash) {
          try {
            updatePassword(result.session.accountId, result.session.upgradedHash);
          } catch {
            // Non-fatal — login still proceeds.
          }
        }
        soundManager.playLevelUp();
        hapticManager.hapticSuccess();
        onLoginSuccess(result.session.accountId, result.session.role);
        return;
      }
      // ── Error mapping ────────────────────────────────────────
      // RATE_LIMITED keeps the service-provided message (informs the user
      // why they have to wait). Everything else collapses to a generic
      // message to prevent account enumeration.
      const code = result.errorCode;
      if (code === 'RATE_LIMITED' || code === 'ACCOUNT_LOCKED') {
        setError(result.error ?? 'Te veel pogingen. Probeer het later opnieuw.');
      } else if (code === 'NETWORK_ERROR') {
        setError(result.error ?? 'Kan geen verbinding maken. Probeer het opnieuw.');
      } else {
        setError(GENERIC_LOGIN_ERROR);
      }
      soundManager.playError();
      hapticManager.hapticError();
      setShakeKey(k => k + 1);
    } finally {
      setIsLoading(false);
    }
  };
  const switchTab = (tab: LoginTab) => {
    soundManager.playClick();
    hapticManager.hapticLight();
    setActiveTab(tab);
    setError('');
    setPassword('');
  };
  return <div className="min-h-screen bg-gradient-to-b from-orange-50 via-amber-50/30 to-white flex items-center justify-center p-4 sm:p-8 relative overflow-hidden" data-id="element-48" data-mp="2a3clxytyjvao">
      {/* Brand Header (Desktop) */}
      <div className="absolute top-6 left-6 hidden md:block z-20" data-id="element-49" data-mp="212oa9as1e70y">
        <BrandLockup size="sm" data-id="element-50" data-mp="3qgba133yquf5" />
      </div>

      <div className="w-full max-w-md relative z-10" data-id="element-51" data-mp="3qhco7gg9zx13">
        {/* Back button */}
        <motion.button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-slate-700 mb-6 transition-colors font-semibold text-sm" initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-52" data-mp="3vel5zfxihz1t">
          <ArrowLeft className="w-4 h-4" data-id="element-53" data-mp="1oq2nk3kaxnyg" /> Terug
        </motion.button>

        {/* Djay guide */}
        <motion.div className="flex flex-col items-center mb-6" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-54" data-mp="22hh6kae7lp5i">
          <motion.div animate={{
          y: [0, -6, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-55" data-mp="27iwrm2gdzvnw">
            <DjayNarrator size="lg" emotion={djayMsg.emotion} animate={true} data-id="element-56" data-mp="36ttjtccpakli" />
          </motion.div>
          <div className="mt-3 w-full max-w-[240px]" data-id="element-57" data-mp="31v07vod2b1ng">
            <AnimatePresence mode="wait" data-id="element-58" data-mp="31t5oiehfvhlh">
              <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 5
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -5
            }} transition={{
              duration: 0.2
            }} data-id="element-59" data-mp="2wvdvfh525pfp">
                <DjayBubble message={djayMsg.text} position="top" autoDismissMs={0} colorScheme="amber" data-id="element-60" data-mp="3fsx7ktmtbo6c" />
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Card */}
        <motion.div key={shakeKey} className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden" initial={{
        opacity: 0,
        y: 20
      }} animate={shakeKey > 0 && shouldAnimate ? {
        // `opacity` en `y` MOETEN hier mee. Door `key={shakeKey}`
        // remount deze kaart bij elke foute poging met
        // `initial={{opacity: 0, y: 20}}`. Zette de shake-variant die
        // niet terug, dan bleef de kaart ONZICHTBAAR: alleen Djay's
        // tekstballon was nog te zien en corrigeren was onmogelijk.
        opacity: 1,
        y: 0,
        x: [-10, 10, -10, 10, 0],
        transition: {
          duration: 0.4
        }
      } : {
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: shakeKey > 0 ? 0 : 0.1
      }} data-id="element-61" data-mp="7ddje9100vy5">
          <div className="p-6 sm:p-8" data-id="element-62" data-mp="wnwv9gsh6y9i">
            {showPicker ? <ProfilePicker profiles={roster} onSelect={handlePickProfile} onSomeoneElse={() => setShowPicker(false)} onForget={handleForgetProfile} data-id="element-63" data-mp="rpllt763eprt" /> : <>
            {/* Title */}
            <h1 className="text-2xl font-black text-slate-900 text-center mb-6" data-id="element-64" data-mp="1fdbvxa491ldd">
              Inloggen
            </h1>

            {/* Tab switcher with animated indicator */}
            <div className="relative flex p-1 bg-slate-100 rounded-2xl mb-6" data-id="element-65" data-mp="1pc13a8yirnfd">
              <motion.div className="absolute top-1 bottom-1 rounded-xl bg-white shadow-sm" initial={false} animate={{
                left: activeTab === 'learner' ? '4px' : '50%',
                width: 'calc(50% - 4px)'
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 30
              }} data-id="element-66" data-mp="1p9yflvkua6dt" />
              <button type="button" onClick={() => switchTab('learner')} className={`relative z-10 flex-1 py-3 text-sm font-bold rounded-xl transition-colors ${activeTab === 'learner' ? 'text-orange-600' : 'text-slate-500'}`} data-id="element-67" data-mp="26kbulxx4sdl1">
                🎒 Leerling
              </button>
              <button type="button" onClick={() => switchTab('adult')} className={`relative z-10 flex-1 py-3 text-sm font-bold rounded-xl transition-colors ${activeTab === 'adult' ? 'text-orange-600' : 'text-slate-500'}`} data-id="element-68" data-mp="1iyezbxjqr8ik">
                👤 Ouder / Docent
              </button>
            </div>

            <form onSubmit={handleLogin} className="space-y-4" data-id="element-69" data-mp="33cfdot5or9cv">
              <AnimatePresence mode="wait" data-id="element-70" data-mp="38biiqdlo1fku">
                {activeTab === 'learner' && <motion.div key="learner-form" initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: 20
                }} transition={{
                  duration: 0.25
                }} className="space-y-4" data-id="element-71" data-mp="18998qpyqdd1w">
                    {/*
                     De koppelcode-tab is verwijderd (14 juli 2026). Een
                     6-tekens code (~30 bits) is te zwak als credential voor
                     toegang tot de gegevens van een kind. Koppelcodes bewijzen
                     nu alleen nog een RELATIE (ouder ↔ kind), geen identiteit.
                     Zie docs/project/SUPABASE_AUTH_MIGRATION.md §3.
                     */}
                    <div className="space-y-4" data-id="element-72" data-mp="2d85uv4ic5qzm">
                      <div data-id="element-73" data-mp="2d7eakpu6vfon">
                        <label htmlFor="login-username" className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-74" data-mp="2i6qt4ye120lf">
                          Gebruikersnaam
                        </label>
                        <div className="relative" data-id="element-75" data-mp="2r6mbbn602vuy">
                          <Input id="login-username" type="text" required value={username} onChange={e => setUsername(e.target.value)} icon={<User className="w-5 h-5" data-id="element-77" data-mp="288jj6zi5oisx" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="bijv. djayden" autoComplete="username" data-id="element-76" data-mp="1uhofe1mikrw7" />
                        </div>
                      </div>
                      <div data-id="element-78" data-mp="2e39xur3nt6zc">
                        <label htmlFor="login-password" className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-79" data-mp="2e1ywsz1lmdbx">
                          Wachtwoord
                        </label>
                        <div className="relative" data-id="element-80" data-mp="205cy5ltwskgg">
                          <Input id="login-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} icon={<KeyRound className="w-5 h-5" data-id="element-82" data-mp="255hjpq9oc5dj" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="••••••••" autoComplete="current-password" data-id="element-81" data-mp="2064tqwniai6q" />
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'adult' && <motion.div key="adult-form" initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.25
                }} className="space-y-4" data-id="element-83" data-mp="3j3rh930yrhym">
                    <div data-id="element-84" data-mp="yd0nubk1crjg">
                      <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-85" data-mp="yebs0che81b3">
                        E-mailadres
                      </label>
                      <div className="relative" data-id="element-86" data-mp="2ppf8c9j2mwwo">
                        <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} icon={<Mail className="w-5 h-5" data-id="element-88" data-mp="2gme6wm55htzc" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="naam@voorbeeld.nl" autoComplete="email" data-id="element-87" data-mp="2pnm410vxkoru" />
                      </div>
                    </div>
                    <div data-id="element-89" data-mp="1szfqfoh16m1f">
                      <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-90" data-mp="1szxyrjgywqpp">
                        Wachtwoord
                      </label>
                      <div className="relative" data-id="element-91" data-mp="1dvwieb8o6t72">
                        <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} icon={<KeyRound className="w-5 h-5" data-id="element-93" data-mp="1nrzjrevp6nsy" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="••••••••" autoComplete="current-password" data-id="element-92" data-mp="1dx7ju4en7ov0" />
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Error message */}
              <AnimatePresence data-id="element-94" data-mp="c7awwms9cb62">
                {error && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="overflow-hidden" data-id="element-95" data-mp="h5m4eqv2y9l4">
                    <div className="p-3 bg-red-50 text-red-700 text-sm font-medium rounded-2xl border border-red-200 flex items-start gap-2" role="alert" aria-live="polite" data-id="element-96" data-mp="2z2n7wbyms7zt">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-97" data-mp="3d0br7fd1v72r" />
                      <span data-id="element-98" data-mp="3hzyaa5fk7hjh">{error}</span>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading} data-id="element-99" data-mp="1io42wkv8hofy">
                {isLoading ? <span className="inline-flex items-center gap-2" data-id="element-100" data-mp="1g47grdhigyu0">
                    <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden="true" data-id="element-101" data-mp="1ba38mqwiekrc" />
                    Even geduld…
                  </span> : 'Inloggen'}
              </Button>

              {/* Contextual help — kid-safe, no dead-end "wachtwoord vergeten" buttons */}
              <p className="text-center text-xs text-slate-400 mt-3 font-medium" data-id="element-102" data-mp="1u0chukqprcsj">
                {activeTab === 'learner' ? 'Wachtwoord vergeten? Vraag je meester of juf.' : 'Wachtwoord vergeten? Vraag een beheerder om een reset.'}
              </p>
            </form>

            {/* Cross-link to Register */}
            {onNavigateToRegister && <div className="mt-6 pt-5 border-t border-slate-100 text-center" data-id="element-103" data-mp="9oevgephff5l">
                <p className="text-slate-500 font-medium text-sm" data-id="element-104" data-mp="b497vx7j29yq">
                  Nog geen account?{' '}
                  <button onClick={onNavigateToRegister} className="text-orange-600 font-bold hover:text-orange-800 hover:underline transition-colors" data-id="element-105" data-mp="g0hu1uk573fe">
                    Maak er een aan
                  </button>
                </p>
              </div>}
              </>}
          </div>
        </motion.div>
      </div>
    </div>;
}